// The Experiments operator tab (spec-14). Lists spec-426's A/B experiments with
// each arm's long-form narrative (experiment_variants.description), drives the
// on/off lifecycle (Activate / Pause / Conclude — each audited with a reason),
// and shows per-arm tallies + a trend, with a drill-down into the assigned users.
// Charts reuse Backstage's shared Nivo + insights theme (std-27); success is shown
// as tallied rates only — no significance figure (dec-4 / spec-109).
import { useCallback, useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { api } from "../api/client";
import { Markdown } from "../components/chat/Markdown";
import {
  insightsTheme,
  integerTicks,
  TOOLTIP_STYLE,
  useChartPalette,
} from "../components/insights/theme";

export type ExperimentStatus = "draft" | "running" | "concluded";
export type AssignmentOutcome = "pending" | "succeeded" | "failed";

export interface VariantTally {
  key: string;
  label: string;
  isControl: boolean;
  behaviour: string;
  description: string | null;
  assigned: number;
  succeeded: number;
  failed: number;
  pending: number;
  decided: number;
  successRate: number | null;
}
export interface TrendPoint {
  date: string;
  succeeded: number;
  failed: number;
  rate: number | null;
}
export interface VariantTrend {
  key: string;
  label: string;
  points: TrendPoint[];
}
export interface ExperimentSummary {
  key: string;
  statement: string;
  status: ExperimentStatus;
  windowDays: number;
  createdAt: string;
  updatedAt: string;
  variants: VariantTally[];
  trend: VariantTrend[];
  decidedTotal: number;
}
export interface AssignmentDetail {
  userId: string;
  email: string;
  name: string | null;
  variantKey: string;
  variantLabel: string;
  outcome: AssignmentOutcome;
  assignedAt: string;
  decidedAt: string | null;
}

// Below this many DECIDED assignments the sample is too small to read into — show a
// soft caveat rather than asserting anything (dec-4: no significance, just a nudge).
const LOW_SIGNAL_THRESHOLD = 20;

const STATUS_BADGE: Record<ExperimentStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-card-hover text-muted" },
  running: { label: "Running", cls: "bg-emerald-500/15 text-emerald-400" },
  concluded: { label: "Concluded", cls: "bg-card-hover text-secondary" },
};

// The operator lifecycle (dec-6). Each non-terminal status offers a fixed set of
// transitions; `concluded` offers none. Copy states the consequence.
interface ActionDef {
  to: ExperimentStatus;
  label: string;
  testid: string;
  consequence: string;
}
const ACTIONS: Record<ExperimentStatus, ActionDef[]> = {
  draft: [
    {
      to: "running",
      label: "Activate",
      testid: "action-activate",
      consequence:
        "Starts assigning new signups 50/50; the treatment arm begins firing.",
    },
  ],
  running: [
    {
      to: "draft",
      label: "Pause",
      testid: "action-pause",
      consequence:
        "Stops new assignment and degrades provisioning back to control, but keeps the experiment re-activatable and verdicts unfrozen.",
    },
    {
      to: "concluded",
      label: "Conclude",
      testid: "action-conclude",
      consequence:
        "Stops new assignment, degrades provisioning back to control, and freezes verdicts. Terminal — re-running means cloning to a new experiment key.",
    },
  ],
  concluded: [],
};

function pct(rate: number | null): string {
  return rate == null ? "—" : `${Math.round(rate * 100)}%`;
}

export function ExperimentsPage() {
  const [experiments, setExperiments] = useState<ExperimentSummary[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { experiments } = await api<{ experiments: ExperimentSummary[] }>(
        "/api/experiments",
      );
      setExperiments(experiments);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return <div className="p-8 text-status-danger-text">Failed to load experiments.</div>;
  }
  if (experiments === null) {
    return <div className="p-8 text-muted">Loading…</div>;
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold text-heading">Experiments</h1>
      <p className="text-sm text-muted">
        The operator console for the A/B experiment construct — see each experiment
        and its arms, switch it on or off, and track per-arm outcomes.
      </p>
      {experiments.length === 0 ? (
        <div className="p-8 text-muted">No experiments yet.</div>
      ) : (
        <div className="space-y-6">
          {experiments.map((exp) => (
            <ExperimentCard key={exp.key} exp={exp} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExperimentCard({
  exp,
  onChanged,
}: {
  exp: ExperimentSummary;
  onChanged: () => Promise<void>;
}) {
  const badge = STATUS_BADGE[exp.status];
  const lowSignal = exp.decidedTotal < LOW_SIGNAL_THRESHOLD;

  return (
    <section
      data-testid="experiment-card"
      data-experiment-key={exp.key}
      data-status={exp.status}
      className="rounded-xl border border-edge bg-surface p-5 space-y-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-mono text-sm font-semibold text-heading">{exp.key}</h2>
            <span
              data-testid="status-badge"
              className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
            >
              {badge.label}
            </span>
            <span className="text-xs text-muted">{exp.windowDays}-day window</span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-secondary">{exp.statement}</p>
        </div>
        <LifecycleControls exp={exp} onChanged={onChanged} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {exp.variants.map((v) => (
          <ArmCard key={v.key} variant={v} />
        ))}
      </div>

      <Results exp={exp} lowSignal={lowSignal} />
      <DrillDown experimentKey={exp.key} />
    </section>
  );
}

function ArmCard({ variant: v }: { variant: VariantTally }) {
  return (
    <div
      data-testid="arm-card"
      data-arm={v.key}
      className="rounded-lg border border-edge bg-page p-4 space-y-2"
    >
      <div className="flex items-center gap-2">
        <span className="rounded bg-card-hover px-1.5 py-0.5 font-mono text-xs text-heading">
          {v.key}
        </span>
        <span className="text-sm font-medium text-heading">{v.label}</span>
        {v.isControl && (
          <span
            data-testid="control-pill"
            className="rounded bg-card-hover px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-muted"
          >
            control
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] text-muted">{v.behaviour}</span>
      </div>
      {/* The long-form A/B narrative (experiment_variants.description), rendered as
          read-only markdown. Null description → label-only, no error (ac-11). */}
      {v.description ? (
        <Markdown>{v.description}</Markdown>
      ) : (
        <p data-testid="arm-label-only" className="text-xs italic text-muted">
          No narrative for this arm.
        </p>
      )}
    </div>
  );
}

function LifecycleControls({
  exp,
  onChanged,
}: {
  exp: ExperimentSummary;
  onChanged: () => Promise<void>;
}) {
  const [pending, setPending] = useState<ActionDef | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const actions = ACTIONS[exp.status];

  if (actions.length === 0) {
    return (
      <span data-testid="terminal-note" className="text-xs italic text-muted">
        Concluded — terminal. Re-running requires a new experiment key.
      </span>
    );
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/experiments/${exp.key}/status`, {
        method: "POST",
        body: JSON.stringify({ to: pending.to, reason: reason.trim() }),
      });
      setPending(null);
      setReason("");
      await onChanged();
    } catch {
      setErr("That change was rejected.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {actions.map((a) => (
          <button
            key={a.to}
            data-testid={a.testid}
            onClick={() => {
              setPending(a);
              setErr(null);
            }}
            className="rounded-md border border-edge bg-page px-3 py-1 text-xs font-medium text-heading hover:bg-card-hover"
          >
            {a.label}
          </button>
        ))}
      </div>
      {pending && (
        <div
          data-testid="confirm-panel"
          className="rounded-lg border border-edge bg-page p-3 text-xs space-y-2"
        >
          <p className="text-secondary">{pending.consequence}</p>
          <textarea
            data-testid="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded in the audit trail)"
            className="w-full rounded border border-edge bg-surface p-2 text-xs text-primary"
            rows={2}
          />
          {err && <p data-testid="action-error" className="text-status-danger-text">{err}</p>}
          <div className="flex items-center gap-2">
            <button
              data-testid="confirm-status"
              disabled={busy}
              onClick={() => void confirm()}
              className="rounded-md bg-emerald-500/15 px-3 py-1 font-medium text-emerald-400 disabled:opacity-50"
            >
              Confirm {pending.label}
            </button>
            <button
              data-testid="cancel-status"
              disabled={busy}
              onClick={() => {
                setPending(null);
                setReason("");
              }}
              className="rounded-md px-3 py-1 text-secondary hover:text-primary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Results({ exp, lowSignal }: { exp: ExperimentSummary; lowSignal: boolean }) {
  const palette = useChartPalette();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted">Results</h3>
        {lowSignal && (
          <span
            data-testid="low-signal"
            className="rounded bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-400"
          >
            Low signal — only {exp.decidedTotal} decided so far; read with care.
          </span>
        )}
      </div>

      {/* Exact values as a table — the chart is the glance, the table is the truth. */}
      <table className="w-full text-left text-sm" data-testid="tally-table">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-muted">
            <th className="py-1 pr-4 font-medium">Arm</th>
            <th className="py-1 pr-4 font-medium">Assigned</th>
            <th className="py-1 pr-4 font-medium">Succeeded</th>
            <th className="py-1 pr-4 font-medium">Failed</th>
            <th className="py-1 pr-4 font-medium">Pending</th>
            <th className="py-1 pr-4 font-medium">Success rate</th>
          </tr>
        </thead>
        <tbody>
          {exp.variants.map((v) => (
            <tr key={v.key} data-testid="tally-row" data-arm={v.key} className="border-t border-edge">
              <td className="py-1 pr-4 text-secondary">
                {v.key} · {v.label}
              </td>
              <td className="py-1 pr-4 text-primary">{v.assigned}</td>
              <td className="py-1 pr-4 text-primary">{v.succeeded}</td>
              <td className="py-1 pr-4 text-primary">{v.failed}</td>
              <td className="py-1 pr-4 text-primary">{v.pending}</td>
              <td className="py-1 pr-4 font-medium text-heading" data-testid="success-rate" data-arm={v.key}>
                {pct(v.successRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-edge bg-page p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-muted">Per-arm tallies</div>
          <div className="relative h-56" data-testid="tally-chart" role="img" aria-label="Per-arm tallies">
            <ResponsiveBar
              data={exp.variants.map((v) => ({
                arm: v.key,
                succeeded: v.succeeded,
                failed: v.failed,
                pending: v.pending,
              }))}
              keys={["succeeded", "failed", "pending"]}
              indexBy="arm"
              groupMode="grouped"
              margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
              padding={0.25}
              theme={insightsTheme}
              colors={[palette.verified, palette.failing, palette.phase.draft]}
              borderRadius={2}
              enableLabel={false}
              axisBottom={{ tickSize: 0, tickPadding: 8 }}
              axisLeft={{ tickSize: 0, tickPadding: 8 }}
              animate
              tooltip={({ id, value, indexValue }) => (
                <div className="rounded-lg px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
                  Arm {String(indexValue)}: {value} {String(id)}
                </div>
              )}
            />
          </div>
        </div>

        <div className="rounded-lg border border-edge bg-page p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-muted">
            Success rate over time
          </div>
          <TrendChart trend={exp.trend} palette={palette} />
        </div>
      </div>
    </div>
  );
}

function TrendChart({
  trend,
  palette,
}: {
  trend: VariantTrend[];
  palette: ReturnType<typeof useChartPalette>;
}) {
  const series = trend
    .filter((t) => t.points.length > 0)
    .map((t, i) => ({
      id: `${t.key} · ${t.label}`,
      color: i === 0 ? palette.human : palette.accent,
      data: t.points.map((p) => ({ x: p.date, y: p.rate == null ? 0 : Math.round(p.rate * 100) })),
    }));

  if (series.length === 0) {
    return (
      <div
        data-testid="trend-chart"
        className="flex h-56 items-center justify-center text-sm text-muted"
      >
        No decided outcomes yet.
      </div>
    );
  }

  return (
    <div className="relative h-56" data-testid="trend-chart" role="img" aria-label="Success rate over time">
      <ResponsiveLine
        data={series}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: 0, max: 100 }}
        margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
        colors={(s) => String(s.color)}
        theme={insightsTheme}
        curve="monotoneX"
        enablePoints
        pointSize={6}
        enableArea={false}
        lineWidth={2.5}
        axisBottom={{ tickSize: 0, tickPadding: 8 }}
        axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: integerTicks(100), format: (v) => `${v}%` }}
        gridYValues={integerTicks(100)}
        enableGridX={false}
        useMesh
        tooltip={({ point }) => (
          <div className="rounded-lg px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
            <div className="font-medium">{String(point.seriesId)}</div>
            <div>
              {String(point.data.xFormatted)}: {String(point.data.yFormatted)}%
            </div>
          </div>
        )}
        animate
      />
    </div>
  );
}

function DrillDown({ experimentKey }: { experimentKey: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<AssignmentDetail[] | null>(null);
  const [error, setError] = useState(false);

  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (rows === null && !error) {
      try {
        const { assignments } = await api<{ assignments: AssignmentDetail[] }>(
          `/api/experiments/${experimentKey}/assignments`,
        );
        setRows(assignments);
      } catch {
        setError(true);
      }
    }
  }

  return (
    <div>
      <button
        data-testid="drill-toggle"
        onClick={() => void toggle()}
        className="text-xs font-medium text-accent hover:underline"
      >
        {open ? "Hide assigned users" : "View assigned users"}
      </button>
      {open && (
        <div data-testid="drill-panel" className="mt-2 overflow-x-auto">
          {error ? (
            <p className="text-xs text-status-danger-text">Failed to load assignments.</p>
          ) : rows === null ? (
            <p className="text-xs text-muted">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-muted">No assignments yet.</p>
          ) : (
            <table className="w-full text-left text-xs" data-testid="assignments-table">
              <thead>
                <tr className="uppercase tracking-wide text-muted">
                  <th className="py-1 pr-4 font-medium">User</th>
                  <th className="py-1 pr-4 font-medium">Arm</th>
                  <th className="py-1 pr-4 font-medium">Outcome</th>
                  <th className="py-1 pr-4 font-medium">Assigned</th>
                  <th className="py-1 pr-4 font-medium">Decided</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.userId}
                    data-testid="assignment-row"
                    data-arm={r.variantKey}
                    className="border-t border-edge"
                  >
                    <td className="py-1 pr-4 text-secondary">{r.name ?? r.email}</td>
                    <td className="py-1 pr-4 text-primary">
                      {r.variantKey} · {r.variantLabel}
                    </td>
                    <td className="py-1 pr-4 text-primary">{r.outcome}</td>
                    <td className="py-1 pr-4 text-muted">{r.assignedAt.slice(0, 10)}</td>
                    <td className="py-1 pr-4 text-muted">
                      {r.decidedAt ? r.decidedAt.slice(0, 10) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
